import { Button, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { Path } from '../../PathBuilder/pathBuilderTypes';
import checkPathName from '../../../utils/validation/checkPathName';
import { assignPathAndWaypointIds } from '../../../utils/path-builder/path-builder-helpers';
import { find } from 'ramda';

interface PathCreationDialogProps {
  onClose: () => void;
  open: boolean;
}

const PathCreationDialog = ({ onClose, open }: PathCreationDialogProps) => {
  const { isEditing, pathId } = useSelector((state: RootState) => state.application.openDialog.options);
  const [pathName, setPathName] = useState<string>('');
  const [path, setPath] = useState<Path>();
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loadedProject } = useSelector((state: RootState) => state.project);

  const findPath = useCallback(
    (id: string) => {
      if (!loadedProject) return;
      const existing = find((p: Path) => p.id === id, loadedProject.paths);
      return existing;
    },
    [loadedProject]
  );

  useEffect(() => {
    if (open) {
      if (!pathId) return;
      const _path = findPath(pathId);
      setPath(_path);
      if (isEditing) {
        const workingName = _path?.name || '';
        setPathName(workingName);
      }
    }
  }, [open, isEditing, pathId, findPath]);

  useEffect(() => {
    if (!open) return;

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [open]);

  const handleInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPathName(event.target.value);
  };

  const handleCreatePath = () => {
    if (!loadedProject) return;

    const originalId = path?.id ?? '';
    const checkedName = checkPathName(pathName, originalId) || pathName;

    let draft: Path;
    if (path) {
      // Editing: preserve existing waypoints and segment speeds; update name and re-assign ids.
      draft = assignPathAndWaypointIds({ ...path, name: checkedName });
    } else {
      // Creating: build default waypoints with placeholder ids; migratePath (via onUpdatePaths)
      // will canonicalize them, but we assign here so the name-based id is set immediately.
      const skeleton: Path = {
        name: checkedName,
        id: checkedName,
        isVisible: true,
        wp: [
          { id: '', x: 0.3, y: 0.3, rad: 0, rot: 0 },
          { id: '', x: 1, y: 0.3, rad: 0, rot: 30 },
        ],
        spd: [1],
      };
      draft = assignPathAndWaypointIds(skeleton);
    }

    let _paths: Array<Path>;
    if (originalId && findPath(originalId)) {
      // Replace the original path (matched by original id) with the renamed/updated one.
      _paths = loadedProject.paths.map((p: Path) => (p.id === originalId ? draft : p));
    } else {
      _paths = [...loadedProject.paths, draft];
    }

    dispatch(projectSlice.actions.onUpdatePaths(_paths));
    dispatch(projectSlice.actions.onUpdateSelectedPathId(draft.id));
    onClose();
  };

  const handleSubmitPath = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleCreatePath();
  };

  const handleCancelPath = () => {
    onClose();
  };

  return (
    <>
      <DialogTitle textAlign="center">
        <Trans i18nKey={'pathCreationDialog.name-your-path'}>Name Your Path</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack component="form" direction="column" spacing={2} marginTop={1} onSubmit={handleSubmitPath}>
          <TextField
            autoFocus
            id="path-name"
            inputRef={inputRef}
            label={t('pathCreationDialog.input.label', 'Path Name')}
            placeholder={t('pathCreationDialog.input.placeholder', 'Untitled Path')}
            value={pathName}
            onChange={handleInput}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Stack direction="row" display="flex" justifyContent="space-between">
            <Button type="button" onClick={handleCancelPath}>
              <Trans i18nKey="pathCreationDialog.button.cancel">Cancel</Trans>
            </Button>

            <Button type="submit">
              <Trans i18nKey="pathCreationDialog.button.create">Create</Trans>
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default PathCreationDialog;
